import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Badge, Card, ErrorNotice, PageHeader, Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import { MobileList } from '@/components/MobileList';
import { DataToolbar, NoResults, SortableTh } from '@/components/table';
import { api, ApiError, type AuditEntry } from '@/lib/api';
import { useTableState, type SortOption } from '@/lib/table';
import { dateTime } from '@/lib/format';
import { useAuditAction } from '@/lib/useLabel';

const AUDIT_SORTS: SortOption<AuditEntry>[] = [
  { key: 'when', labelKey: 'audit.when', value: (e) => e.created_at, defaultDirection: 'desc' },
  { key: 'actor', labelKey: 'audit.actor', value: (e) => e.actor_name },
  { key: 'action', labelKey: 'audit.action', value: (e) => e.action },
];

const ACTIONS = [
  '',
  'shift.adjusted',
  'shift.clock_in',
  'shift.clock_out',
  'document.downloaded',
  'document.uploaded',
  'user.created',
  'user.suspended',
  'user.login_created',
  'user.password_reset',
  'job.assigned',
  'site.updated',
  'settings.changed',
  'calendar.event_created',
  'calendar.event_updated',
  'calendar.event_deleted',
  'leave.requested',
  'leave.cancelled',
  'leave.approved',
  'leave.rejected',
];

/** The handful that deserve to stand out in a long list. */
function toneFor(action: string): 'neutral' | 'warning' | 'danger' | 'primary' {
  if (action === 'shift.adjusted' || action === 'settings.changed') return 'warning';
  if (action === 'user.login_created' || action === 'user.password_reset') return 'warning';
  if (action === 'leave.rejected' || action === 'calendar.event_deleted') return 'danger';
  if (action === 'leave.approved') return 'primary';
  if (action.startsWith('user.') || action === 'document.deleted') return 'danger';
  if (action === 'document.downloaded') return 'primary';
  return 'neutral';
}

export function AuditPage() {
  const { t } = useI18n();
  const actionLabel = useAuditAction();
  const [action, setAction] = useState('');

  const audit = useQuery({
    queryKey: ['audit', action],
    queryFn: () => api.audit({ action: action || undefined, limit: 200 }),
  });

  // Searching the entity id matters here: an investigation usually starts from an id
  // somebody was given, not from a name.
  const table = useTableState<AuditEntry>({
    rows: audit.data?.entries ?? [],
    search: (e) => [e.actor_name, actionLabel(e.action), e.entity_type, e.entity_id],
    sorts: AUDIT_SORTS,
    defaultSortKey: 'when',
    storageKey: 'audit',
  });

  return (
    <>
      <PageHeader
        title={t('audit.title')}
        subtitle={t('audit.subtitle')}
      />

      <Card className="mb-4 bg-[var(--color-primary-soft)] border-[var(--color-primary)]">
        <p className="text-sm">{t('audit.immutableNotice')}</p>
      </Card>

      {audit.isLoading ? (
        <Spinner />
      ) : audit.error ? (
        /* A permission rule is not a fault, and should not be reported as one — least
           of all in a raw server string that ignores the chosen language. */
        audit.error instanceof ApiError && audit.error.code === 'FORBIDDEN' ? (
          <Card>
            <h2 className="font-bold mb-1">{t('audit.adminOnly')}</h2>
            <p className="text-sm text-muted leading-relaxed">{t('audit.adminOnlyHint')}</p>
          </Card>
        ) : (
          <ErrorNotice error={audit.error} />
        )
      ) : (
        <>
        <DataToolbar
          state={table}
          searchPlaceholder={t('audit.searchPlaceholder')}
          countOne="audit.countOne"
          countMany="audit.countMany"
          extra={
            <label className="flex items-center gap-2">
              <span className="sr-only">{t('audit.action')}</span>
              <select
                className="input w-auto"
                value={action}
                onChange={(e) => setAction(e.target.value)}
              >
                {ACTIONS.map((a) => (
                  <option key={a} value={a}>
                    {a === '' ? t('common.all') : actionLabel(a)}
                  </option>
                ))}
              </select>
            </label>
          }
        />

        <Card padded={false}>
          <div className="lg:hidden p-2">
            <MobileList
              rows={table.rows}
              keyOf={(e) => String(e.id)}
              title={(e) => actionLabel(e.action)}
              subtitle={(e) => `${e.actor_name ?? t('audit.system')} \u00b7 ${dateTime(e.created_at)}`}
              badges={(e) => <Badge tone={toneFor(e.action)}>{e.entity_type}</Badge>}
              fields={[
                { label: t('audit.entity'), value: (e) => e.entity_id ?? null },
                {
                  label: t('common.details'),
                  value: (e) =>
                    e.after ? (
                      <pre className="whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed">
                        {JSON.stringify(e.after, null, 2)}
                      </pre>
                    ) : null,
                },
              ]}
            />
          </div>

          <div className="table-scroll max-lg:hidden">
            <table className="table-wide">
              <thead>
                <tr>
                  <SortableTh state={table} sortKey="when">
                    {t('audit.when')}
                  </SortableTh>
                  <SortableTh state={table} sortKey="actor">
                    {t('audit.actor')}
                  </SortableTh>
                  <SortableTh state={table} sortKey="action">
                    {t('audit.action')}
                  </SortableTh>
                  <th className="th">{t('audit.entity')}</th>
                  <th className="th">{t('common.details')}</th>
                </tr>
              </thead>
              <tbody>
                {table.rows.map((e) => (
                  <tr key={e.id} className="hover:bg-surface-alt align-top">
                    <td className="td tabular text-muted whitespace-nowrap">{dateTime(e.created_at)}</td>
                    <td className="td font-medium whitespace-nowrap">{e.actor_name ?? t('audit.system')}</td>
                    <td className="td">
                      <Badge tone={toneFor(e.action)}>{actionLabel(e.action)}</Badge>
                    </td>
                    <td className="td text-muted text-xs">
                      {e.entity_type}
                      {e.entity_id ? (
                        <span className="block font-mono text-[11px]">{e.entity_id.slice(0, 8)}…</span>
                      ) : null}
                    </td>
                    <td className="td text-xs text-muted max-w-md">
                      {e.after ? (
                        <pre className="whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed">
                          {JSON.stringify(e.after, null, 0).slice(0, 220)}
                        </pre>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {table.rows.length === 0 ? <NoResults onClear={table.clear} /> : null}
        </Card>
        </>
      )}
    </>
  );
}
